import { CalendarEvent } from "../../api/types.tsx";
import { Player, Participant, Set, MatchDTO } from "./matchTypes.tsx";

export interface MatchState {
  match: MatchDTO;
  notes: string;
  sets: Set[];
  participants: Participant[];
  players: Player[];
}

export type MatchAction =
  | { type: "SET_PLAYERS"; players: Player[] }
  | { type: "UPDATE_NOTES"; notes: string }

  | { type: "ADD_SET" }
  | { type: "REMOVE_SET"; setNumber: number }

  | { type: "UPDATE_SET"; setNumber: number; field: keyof Set; value: any }

  // Legacy — opzionale, puoi anche rimuoverlo
  | { type: "ADD_PLAYER"; setNumber: number }

  // NEW: aggiungi un player a un set con ruolo e starter
  | {
      type: "ADD_PLAYER_TO_SET";
      setNumber: number;
      playerId: number;
      role: string;
      starter: boolean;
    }

  // NEW: rimuovi un partecipante dal set
  | {
      type: "REMOVE_PLAYER";
      setNumber: number;
      playerId: number;
    }

  // NEW: aggiorna un campo di un partecipante
  | {
      type: "UPDATE_PLAYER";
      setNumber: number;
      playerId: number;
      field: keyof Participant;
      value: any;
    }

  | { type: "RESET_STATE"; payload: MatchState };

export const initialMatchState = (event: CalendarEvent, players: Player[]): MatchState => ({
  match: {
    id: 0,
    date: event?.date,
    opponent: event?.title,
    finalResult: "",
    competitionType: event?.type,
    note: event?.note ?? "",
    sets: [],
    participants: [],
  },
  notes: event?.note ?? "",
  sets: [],
  participants: [],
  players,
});

// Assumo tipi semplificati:
// action può avere: playerId?: number, setNumber?: number, index?: number, role?: string, starter?: boolean
export const matchReducer = (state: MatchState, action: MatchAction): MatchState => {
  switch (action.type) {
    case "SET_PLAYERS":
      return { ...state, players: action.players };

    case "UPDATE_NOTES":
      return { ...state, notes: action.notes };

    case "ADD_SET": {
      const newSet: Set = {
        id: 0,
        setNumber: state.sets.length + 1,
        teamScore: 21,
        opponentScore: 21,
      };
      return { ...state, sets: [...state.sets, newSet] };
    }

    case "REMOVE_SET": {
      return {
        ...state,
        sets: state.sets.filter((s) => s.setNumber !== action.setNumber),
        participants: state.participants.filter((p) => p.setNumber !== action.setNumber),
      };
    }

    case "UPDATE_SET": {
      const sets = state.sets.map((s) =>
        s.setNumber === action.setNumber ? { ...s, [action.field]: action.value } : s
      );
      return { ...state, sets };
    }



    // Preferred: add a specific player to a set (what the UI is dispatching)
    case "ADD_PLAYER_TO_SET": {
      if (typeof action.playerId === "undefined" || typeof action.setNumber === "undefined") {
        return state;
      }
      const alreadyPresent = state.participants.some(
        (p) => p.playerId === action.playerId && p.setNumber === action.setNumber
      );
      if (alreadyPresent) return state;
      const newParticipant: Participant = {
        id: 0,
        setNumber: action.setNumber,
        playerId: action.playerId,
        role: action.role ?? "",
        starter: action.starter ?? false,
      };
      return { ...state, participants: [...state.participants, newParticipant] };
    }

    // REMOVE_PLAYER: support both index-based and playerId+setNumber
    case "REMOVE_PLAYER": {
      if (typeof action.playerId !== "undefined" && typeof action.setNumber !== "undefined") {
        // remove by playerId + setNumber
        const participants = state.participants.filter(
          (p) => !(p.playerId === action.playerId && p.setNumber === action.setNumber)
        );
        return { ...state, participants };
      }
      return state;
    }

    case "UPDATE_PLAYER": {
      const participants = [...state.participants];
     

      // alternatively support update by playerId+setNumber
      if (typeof action.playerId !== "undefined" && typeof action.setNumber !== "undefined") {
        const idx = participants.findIndex(
          (p) => p.playerId === action.playerId && p.setNumber === action.setNumber
        );
        if (idx >= 0) {
          participants[idx] = { ...participants[idx], [action.field]: action.value };
          return { ...state, participants };
        }
      }

      return state;
    }

    case "RESET_STATE":
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }

};
